import React, { useState, useEffect } from 'react';
import { FaClock } from 'react-icons/fa';
import { getApiUrl } from '../../utils/apiConfig';
import apiService from '../../services/apiService';

const TestClockTimeIndicator = ({ userEmail, userId }) => {
  const [testClockTime, setTestClockTime] = useState(null);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clockType, setClockType] = useState(null); // 'stripe' or 'paygo'

  useEffect(() => {
    if (!userEmail || !userId) {
      setLoading(false);
      return;
    }

    fetchTestClocksByCurrentUser();
  }, [userEmail, userId]);

  const fetchTestClocksByCurrentUser = async () => {
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setTestClockTime(null);
        setLoading(false);
        return;
      }

      // First check for Stripe test clocks (subscription users)
      try {
        const testClocksUrl = getApiUrl(`/testing/test-clocks/user/${userId}`);
        const testClocksResponse = await fetch(testClocksUrl);
        const result = await testClocksResponse.json();

        if (testClocksResponse.ok && result.test_clocks && result.test_clocks.length > 0) {
          // Find active test clock
          const activeClock = result.test_clocks.find(clock => clock.status !== 'deleted');
          if (activeClock) {
            setTestClockTime(activeClock.frozen_time);
            setIsAdvancing(activeClock.status === 'advancing');
            setClockType('stripe');
            return; // Exit early, found Stripe test clock
          }
        }
      } catch (error) {
        // Stripe test clocks not available
      }

      // If no Stripe test clocks found, check for PayGo test clocks (admin-only endpoint)
      if (import.meta.env.VITE_BUILD_TARGET !== 'public') {
        try {
          const paygoResult = await apiService.get(`/admin/paygo-test-clocks?user_id=${userId}`);

          if (paygoResult.data && paygoResult.data.length > 0) {
            // Find active PayGo test clock
            const activeClock = paygoResult.data.find(clock => clock.status === 'active');
            if (activeClock) {
              setTestClockTime(activeClock.simulated_time);
              setIsAdvancing(false); // PayGo clocks don't have 'advancing' status
              setClockType('paygo');
              return;
            }
          }
        } catch (error) {
          // PayGo test clocks not available
        }
      }

      setTestClockTime(null);
      setClockType(null);
    } catch (error) {
      console.error('Error fetching test clock time:', error);
      setTestClockTime(null);
      setClockType(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Don't show anything if loading or no test clock time
  if (loading || !testClockTime) {
    return null;
  }

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-50 border-b border-yellow-200 px-4 py-1">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <FaClock className="text-yellow-600 mr-1 w-3 h-3" />
          <span className="text-xs text-yellow-800">
            <span className="font-medium">Calculated Date: </span>
            <span className="font-mono">{formatDate(testClockTime)}</span>
            {isAdvancing && (
              <span className="ml-1 text-yellow-600">(advancing...)</span>
            )}
            <span className="ml-2 text-yellow-600 text-xs">
              ({clockType === 'paygo' ? 'PayGo' : 'Stripe'} test clock)
            </span>
          </span>
        </div>
      </div>
      {/* Spacer to push content down */}
      <div className="h-7"></div>
    </>
  );
};

export default TestClockTimeIndicator;